import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@/core/theme';
import { I18nProvider, currentLanguage, loadLanguage } from '@/core/i18n';
import { DrillProvider } from '@/components/drill';
import { SessionProvider } from '@/core/session';
import { attachPushTapListener } from '@/core/push';
import { ConfirmProvider } from '@/components/confirm';
import { ExportLanguageProvider } from '@/components/exportLanguage';
import { router } from '@/app/routes';
import '@/styles/app.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Field phones move in and out of coverage constantly. Refetching on every
      // window focus would hammer a camp's single access point at shift change.
      refetchOnWindowFocus: false,
      staleTime: 60_000,
      retry: 2,

      /* Offline-first, everywhere.
       *
       * The default is 'online', which does not merely fail a query without a
       * connection — it never runs it at all. The query sits paused, the
       * function never executes, and any fallback to a local cache inside it is
       * unreachable. The face kiosk read as "0 faces on this device" on a site
       * with no signal for exactly this reason: the descriptors were cached on
       * the handset and the code that reads them was never called.
       *
       * 'offlineFirst' runs the query once regardless. What comes back from the
       * cache is served; what fails falls through to whatever the query itself
       * does about it. React Query still refetches on reconnect, so a device
       * that finds signal catches itself up without being asked.
       */
      networkMode: 'offlineFirst',
    },
    mutations: {
      // Same reasoning: a scan queued on the device must be attempted rather
      // than held back because the radio is down.
      networkMode: 'offlineFirst',
    },
  },
});

const rootElement = document.getElementById('root');
if (!rootElement) throw new Error('Root element #root was not found in index.html');

/* -----------------------------------------------------------------------------
 * The dictionary, before the first frame
 * -----------------------------------------------------------------------------
 * Arabic is no longer in the main bundle — it is fetched, and only by the
 * sessions that read it. That saves every reader 53 kB gzipped, and it
 * introduces exactly one risk worth handling here.
 *
 * The language is remembered on the device precisely so the app opens in the
 * right language and the right direction on the *first* paint, before any query
 * has returned. Rendering while the dictionary was still in flight would undo
 * that: an Arabic user would get a frame of English laid out right-to-left, and
 * then the words would fill in. That is the exact half-second of broken-looking
 * interface the stored language exists to prevent.
 *
 * So the fetch is started immediately — while the module graph above is still
 * being evaluated — and awaited before anything is mounted. An English session
 * awaits an already-resolved promise and pays nothing.
 *
 * If it fails, the app renders anyway. English with a missing-translation
 * warning is a working application; a blank screen because a stylesheet of
 * words did not arrive is not.
 * -------------------------------------------------------------------------- */
const dictionary = loadLanguage(currentLanguage());

/* Before anything renders.
   -------------------------------------------------------------------------
   Tapping a notification while the app is closed launches it, and Capacitor
   fires the tap as the plugin comes up — before React exists. Attached from
   a component effect, as it was, that event had nowhere to go and every
   notification opened the same screen. The URL is held here until the router
   is ready to take it. */
void attachPushTapListener();

function mount() {
  createRoot(rootElement!).render(
  <StrictMode>
    <ThemeProvider>
      <I18nProvider>
        <QueryClientProvider client={queryClient}>
          <SessionProvider>
            {/* Inside the session so a confirmation can outlive a route
                change, and above the router so any screen can ask. */}
            <ConfirmProvider>
              {/* Inside the session too: it reads whether this company has a
                  second language at all, and skips the question when it does
                  not. */}
              <ExportLanguageProvider>
                {/* The stack, outside the router, so a panel survives a route
                    change and a route change never unmounts the page a panel is
                    sitting over.

                    Only the stack. What draws the panel is mounted inside the
                    router, in NativeShell, because a panel links to the page it
                    summarises and `<Link>` throws without a router above it. */}
                <DrillProvider>
                  <RouterProvider router={router} />
                </DrillProvider>
              </ExportLanguageProvider>
            </ConfirmProvider>
          </SessionProvider>
        </QueryClientProvider>
      </I18nProvider>
    </ThemeProvider>
  </StrictMode>,
  );
}

void dictionary.then(mount, mount);
